import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  Package,
  Key,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Users,
  LayoutDashboard,
  BookOpen,
  PlayCircle,
  Shield,
  ShieldCheck,
  KeyRound,
  Grid3x3,
  ScrollText,
  CloudCog,
  Search,
  Sun,
  Moon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BrandMark } from '@/components/brand';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { CommandPalette } from '@/components/command-palette';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth, useTheme } from '@/hooks';
import { cn } from '@/lib/utils';

const mainNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Organizations', href: '/organizations', icon: Building2 },
  { name: 'Products', href: '/products', icon: Package },
  { name: 'OAuth Clients', href: '/clients', icon: Key },
  { name: 'My Access', href: '/access', icon: ShieldCheck },
];

const adminNavigation = [
  { name: 'Users', href: '/users', icon: Users, adminOnly: true },
  { name: 'Access Matrix', href: '/access-matrix', icon: Grid3x3, adminOnly: true },
  { name: 'Audit Log', href: '/audit', icon: ScrollText, adminOnly: true },
  { name: 'Signing Keys', href: '/keys', icon: KeyRound, adminOnly: true },
  { name: 'Hosted MCP', href: '/mcp-access', icon: CloudCog, adminOnly: true },
];

const settingsNavigation = [{ name: 'Settings', href: '/settings', icon: Settings }];

const resourceLinks = [
  { name: 'API Docs', href: '/docs', icon: BookOpen },
  { name: 'OAuth Playground', href: '/playground', icon: PlayCircle },
];

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setJumpOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const jumpItems = [
    ...mainNavigation,
    ...(user?.is_super_admin ? adminNavigation : []),
    ...settingsNavigation,
    ...resourceLinks,
  ];
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-background/70 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[248px] bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 max-lg:shadow-lg',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <BrandMark />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Close navigation"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-2.5 py-3">
            {/* Main Navigation */}
            <div className="space-y-0.5">
              {mainNavigation.map((item) => {
                const isActive =
                  location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'relative flex min-h-9 items-center gap-2.5 rounded-[7px] px-2.5 text-[13.5px] font-medium transition-colors',
                      isActive
                        ? 'bg-foreground/[0.05] text-foreground before:absolute before:-left-2.5 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-primary'
                        : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="h-[17px] w-[17px]" />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Admin Section - Only visible to super admins */}
            {user?.is_super_admin && (
              <div className="mt-[18px]">
                <p className="mb-1.5 px-2.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  Admin
                </p>
                <div className="space-y-0.5">
                  {adminNavigation.map((item) => {
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'relative flex min-h-9 items-center gap-2.5 rounded-[7px] px-2.5 text-[13.5px] font-medium transition-colors',
                          isActive
                        ? 'bg-foreground/[0.05] text-foreground before:absolute before:-left-2.5 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-primary'
                        : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="h-[17px] w-[17px]" />
                        {item.name}
                        <Shield className="ml-auto h-3 w-3 text-muted-foreground" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="mt-[18px]">
              <div className="space-y-0.5">
                {settingsNavigation.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'relative flex min-h-9 items-center gap-2.5 rounded-[7px] px-2.5 text-[13.5px] font-medium transition-colors',
                        isActive
                        ? 'bg-foreground/[0.05] text-foreground before:absolute before:-left-2.5 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-primary'
                        : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="h-[17px] w-[17px]" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Resource Links */}
            <div className="mt-[18px]">
              <p className="mb-1.5 px-2.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                Resources
              </p>
              <div className="space-y-0.5">
                {resourceLinks.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'relative flex min-h-9 items-center gap-2.5 rounded-[7px] px-2.5 text-[13.5px] font-medium transition-colors',
                        isActive
                        ? 'bg-foreground/[0.05] text-foreground before:absolute before:-left-2.5 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-primary'
                        : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="h-[17px] w-[17px]" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* User section */}
          <div className="border-t border-border p-2.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-auto w-full justify-start gap-2.5 rounded-lg p-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture ?? undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                      {getInitials(user?.name ?? null, user?.email ?? '')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left truncate">
                    <p className="text-[13px] font-semibold text-foreground truncate">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-[248px]">
        {/* Top bar */}
        <header className="app-topbar sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-3.5 backdrop-blur-md lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            aria-label="Open navigation"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Breadcrumbs />
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setJumpOpen(true)}
            aria-label="Jump to a page"
            aria-keyshortcuts="Meta+K Control+K"
            className="flex h-[34px] shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-[13px] text-muted-foreground transition-colors hover:border-foreground/30 sm:min-w-[240px]"
          >
            <Search className="h-4 w-4" />
            <span className="hidden flex-1 text-left sm:inline">Jump to…</span>
            <kbd className="hidden rounded border border-border px-1.5 py-px font-mono text-[11px] sm:inline">
              {isMac ? '\u2318K' : 'Ctrl K'}
            </kbd>
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>
        <CommandPalette open={jumpOpen} onOpenChange={setJumpOpen} items={jumpItems} />

        {/* Page content */}
        <main className="mx-auto w-full max-w-[1240px] px-4 pb-14 pt-5 lg:px-6 lg:pb-16 lg:pt-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
